import React, { useCallback, useMemo, useState, ReactNode } from 'react';
import { Layout } from '../../components/layout';
import { DataTable, Modal, Button, Column, KeyboardShortcuts, ContextMenuAction } from '../../components';
import { areRequiredFieldsFilled } from '../../utils/validation';
import { useTableKeyboardShortcuts } from '../../hooks/useTableKeyboardShortcuts';
import { useBaseCRUD } from '../../hooks/useBaseCRUD';
import { handleDelete } from '../../utils/deleteUtils';

// Navigation context passed to onRowDoubleClick
export interface NavigationContext {
  searchQuery: string;
  searchColumn: string;
  loadedCount: number;
}

interface BaseCRUDPageProps<T, TCreate, TUpdate> {
  // Page configuration
  title: string;
  entityName: string;
  columns: Column[];

  // Service methods
  service: {
    getAll: (params: any) => Promise<T[]>;
    search?: (query: string, column: string, params: any) => Promise<T[]>;
    create: (data: TCreate) => Promise<T>;
    update: (id: string, data: TUpdate) => Promise<T>;
    delete: (id: string) => Promise<any>;
    bulkDelete: (ids: string[]) => Promise<any>;
    bulkArchive?: (ids: string[]) => Promise<any>;
    getFilterOptions?: (column: string, filters?: Record<string, string[]>, extraParams?: any, signal?: AbortSignal) => Promise<any[]>;
  };

  // Form configuration
  formFields: ReactNode | ((fieldErrors: Record<string, string>) => ReactNode);
  formData: any;
  setFormData: (data: any) => void;
  requiredFields: string[];
  getInitialFormData: () => any;

  // Transform functions
  toCreateData: (formData: any) => TCreate;
  toUpdateData: (formData: any, original: T) => TUpdate;
  toFormData: (entity: T) => any;

  // Modal configuration
  modalTitle?: (isEdit: boolean) => { title: string; subtitle: string };
  modalIcon?: ReactNode;
  modalSize?: 'sm' | 'md' | 'lg' | 'xl';

  // Optional customization
  pageSize?: number;
  defaultSearchColumn?: string;
  additionalHeaderContent?: ReactNode | ((selectedRowId?: string) => ReactNode);
  onRowDoubleClick?: (entity: T, context: NavigationContext) => void;
  customDeleteConfirm?: (entity: T) => string;
  readonly?: boolean;
  enableBulkArchive?: boolean;
  // Initial search state (for restoring after navigation)
  initialSearchQuery?: string;
  initialSearchColumn?: string;
  // Restore selected row after navigation
  initialSelectedRowId?: string;
  initialLoadCount?: number;
  // Server-side filtering
  enableServerSideFilters?: boolean;
  extraFilterParams?: Record<string, any>; // Extra params for filter options (e.g., is_archived, is_booking)
}

export function BaseCRUDPage<T extends { id: string }, TCreate, TUpdate>({
  title,
  entityName,
  columns,
  service,
  formFields,
  formData,
  setFormData,
  requiredFields,
  getInitialFormData,
  toCreateData,
  toUpdateData,
  toFormData,
  modalTitle,
  modalIcon,
  modalSize = 'lg',
  pageSize = 100,
  defaultSearchColumn,
  additionalHeaderContent,
  onRowDoubleClick,
  customDeleteConfirm,
  readonly = false,
  enableBulkArchive = false,
  initialSearchQuery,
  initialSearchColumn,
  initialSelectedRowId,
  initialLoadCount,
  enableServerSideFilters = false,
  extraFilterParams
}: BaseCRUDPageProps<T, TCreate, TUpdate>) {
  const resolvedDefaultSearchColumn = useMemo(() => {
    if (defaultSearchColumn) {
      return defaultSearchColumn;
    }
    const firstSearchable = columns.find(c => c.searchable);
    return firstSearchable ? firstSearchable.key : '';
  }, [columns, defaultSearchColumn]);

  // Determine the first sortable column for default sorting
  const resolvedDefaultSortColumn = useMemo(() => {
    const firstSortable = columns.find(c => c.sortable);
    return firstSortable ? firstSortable.key : undefined;
  }, [columns]);

  // Generate tableId for consistent localStorage key
  const tableId = `${entityName}-table`;

  const {
    data,
    loading,
    hasMore,
    searchQuery,
    searchColumn,
    setSearchQuery,
    setSearchColumn,
    sortColumn,
    sortDirection,
    columnFilters,
    setColumnFilters,
    handleSort,
    loadMore,
    refresh,
    getFilterOptions
  } = useBaseCRUD<T>({
    service,
    tableId,
    pageSize,
    defaultSearchColumn: resolvedDefaultSearchColumn,
    defaultSortColumn: resolvedDefaultSortColumn,
    initialSearchQuery,
    initialSearchColumn: initialSearchColumn || resolvedDefaultSearchColumn,
    initialLoadCount,
    enableServerSideFilters,
    extraFilterParams
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEntity, setEditingEntity] = useState<T | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(initialSelectedRowId);
  const [scrollToRowId, setScrollToRowId] = useState<string | undefined>(initialSelectedRowId);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Handle search
  const handleSearch = useCallback((query: string, column: string) => {
    setSearchQuery(query);
    setSearchColumn(column);
  }, [setSearchQuery, setSearchColumn]);

  // Handle create
  const handleCreate = () => {
    setIsEditMode(false);
    setEditingEntity(null);
    setFormData(getInitialFormData());
    setFieldErrors({});
    setIsModalOpen(true);
  };

  // Handle edit
  const handleEdit = (entity: T) => {
    setIsEditMode(true);
    setEditingEntity(entity);
    setFormData(toFormData(entity));
    setFieldErrors({});
    setIsModalOpen(true);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({}); // Clear previous errors
    
    try {
      if (isEditMode && editingEntity) {
        const updateData = toUpdateData(formData, editingEntity);
        await service.update(editingEntity.id, updateData);
        setIsModalOpen(false);
        refresh();
      } else {
        const createData = toCreateData(formData);
        const createdEntity = await service.create(createData);
        setIsModalOpen(false);
        // Set the newly created entity as selected and scroll target
        setSelectedRowId(createdEntity.id);
        setScrollToRowId(createdEntity.id);
        refresh();
      }
    } catch (error: any) {
      console.error(`Error saving ${entityName}:`, error);
      
      // Check if it's the new uniform error format
      const errorDetail = error?.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error === 'duplicate_value') {
        // New uniform format: { error: "duplicate_value", field: "field_name", message: "..." }
        const fieldName = errorDetail.field;
        const message = errorDetail.message || 'Duplicate value, please choose another one';
        
        // Try to match the field in formData keys
        const normalizedField = fieldName.toLowerCase().replace(/_/g, '');
        const matchingKey = Object.keys(formData).find(key => 
          key.toLowerCase().replace(/_/g, '') === normalizedField
        );
        
        if (matchingKey) {
          setFieldErrors({ [matchingKey]: message });
        } else {
          // If field not found, set a generic error
          setFieldErrors({ _general: message });
        }
      } else {
        // Fallback for other errors - show generic error message
        const errorMessage = typeof errorDetail === 'string' 
          ? errorDetail 
          : (error?.message || `Failed to save ${entityName}`);
        setFieldErrors({ _general: errorMessage });
      }
    }
  };

  // Handle delete
  const handleDeleteEntity = async (entity: T) => {
    await handleDelete(async () => {
      await service.delete(entity.id);
      refresh();
    });
  };

  // Handle bulk delete
  const handleBulkDelete = async (selectedIds: string[]) => {
    await handleDelete(async () => {
      await service.bulkDelete(selectedIds);
      refresh();
    }, `Are you sure you want to delete ${selectedIds.length} ${entityName}s?`);
  };

  // Handle bulk archive
  const handleBulkArchive = async (selectedIds: string[]) => {
    if (window.confirm(`Are you sure you want to archive ${selectedIds.length} ${entityName}s?`)) {
      try {
        await service.bulkArchive?.(selectedIds);
        refresh();
      } catch (error) {
        console.error(`Error bulk archiving ${entityName}s:`, error);
      }
    }
  };

  // Get selected entity
  const selectedEntity = data.find(e => e.id === selectedRowId);

  // Keyboard shortcuts
  useTableKeyboardShortcuts({
    onDelete: readonly ? undefined : handleDeleteEntity,
    onBulkDelete: readonly ? undefined : handleBulkDelete,
    onCreate: readonly ? undefined : handleCreate,
    selectedRow: selectedEntity,
    selectedRows: selectedRows,
    enabled: !isModalOpen && !readonly
  });

  // Get modal title
  const getModalTitle = () => {
    if (modalTitle) {
      return modalTitle(isEditMode);
    }
    return {
      title: isEditMode ? `Edit ${entityName}` : `Create New ${entityName}`,
      subtitle: isEditMode ? `Update ${entityName} information` : `Add a new ${entityName} to the system`
    };
  };

  const { title: modalTitleText, subtitle: modalSubtitle } = getModalTitle();

  return (
    <Layout>
      <div className="space-y-8">
        {/* Page Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {data.length} {data.length === 1 ? 'record' : 'records'} loaded
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {typeof additionalHeaderContent === 'function' 
                ? additionalHeaderContent(selectedRowId) 
                : additionalHeaderContent}
              {!readonly && (
                <KeyboardShortcuts
                  shortcuts={[
                    { key: 'D', label: 'Delete' },
                    { key: 'N', label: 'New' }
                  ]}
                />
              )}
            </div>
          </div>
        </div>

        {/* Data Table */}
        <DataTable
          tableId={tableId}
          data={data}
          columns={columns}
          loading={loading}
          onLoadMore={loadMore}
          hasMore={hasMore}
          onSearch={handleSearch}
          onSort={(column, direction) => handleSort(column)}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          serverSideFilters={enableServerSideFilters}
          columnFilters={columnFilters}
          onFiltersChange={setColumnFilters}
          getFilterOptions={getFilterOptions}
          onCreate={readonly ? undefined : handleCreate}
          onEdit={readonly ? undefined : handleEdit}
          onDelete={readonly ? undefined : handleDeleteEntity}
          onBulkDelete={readonly ? undefined : handleBulkDelete}
          onBulkArchive={readonly || !enableBulkArchive ? undefined : handleBulkArchive}
          onSelectionChange={setSelectedRows}
          onRowClick={(entity) => setSelectedRowId(entity.id)}
          onRowDoubleClick={readonly ? (entity) => onRowDoubleClick?.(entity, { searchQuery, searchColumn, loadedCount: data.length }) : (entity) => {
            if (onRowDoubleClick) {
              onRowDoubleClick(entity, { searchQuery, searchColumn, loadedCount: data.length });
            } else {
              handleEdit(entity);
            }
          }}
          selectedRowId={selectedRowId}
          scrollToRowId={scrollToRowId}
          emptyMessage={`No ${entityName}s found`}
          pageSize={pageSize}
          actions={readonly
            ? { create: false, edit: false, delete: false, bulkDelete: false, bulkArchive: false }
            : { create: true, edit: true, delete: true, bulkDelete: true, bulkArchive: enableBulkArchive }
          }
          contextMenuActions={(() => {
            const actions: ContextMenuAction<T>[] = [];
            if (onRowDoubleClick) {
              actions.push({
                label: 'Open',
                icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                ),
                onClick: (entity) => onRowDoubleClick(entity, { searchQuery, searchColumn, loadedCount: data.length })
              });
            }
            if (!readonly) {
              actions.push({
                label: 'Edit',
                icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ),
                onClick: handleEdit
              });
            }
            return actions.length > 0 ? actions : undefined;
          })()}
        />

        {/* Create/Edit Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={
            <div className="flex items-center space-x-2">
              {modalIcon || (
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{modalTitleText}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{modalSubtitle}</p>
              </div>
            </div>
          }
          size={modalSize}
          footer={
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 px-6 py-2.5 font-medium"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit(new Event('submit') as any)}
                disabled={!areRequiredFieldsFilled(formData, requiredFields)}
                className={`px-6 py-2.5 font-medium shadow-md transition-all duration-200 ${
                  areRequiredFieldsFilled(formData, requiredFields)
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isEditMode ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  )}
                </svg>
                {isEditMode ? `Update ${entityName}` : `Create ${entityName}`}
              </Button>
            </div>
          }
        >
          <form onSubmit={handleSubmit}>
            {/* General error message for duplicates */}
            {fieldErrors._general && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-red-700 dark:text-red-300">{fieldErrors._general}</span>
              </div>
            )}
            {typeof formFields === 'function' ? formFields(fieldErrors) : formFields}
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
